// Port of webapp/src/components/dialog.tsx + modal.tsx (the centered card
// dialog with a title row and a footer of actions).

import {ReactElement, ReactNode, useEffect} from 'react'
import classnames from 'classnames'
import RootPortal from './rootPortal'
import {Icon} from './icons'

type DialogProps = {
    title: ReactNode
    onClose: () => void
    buttons?: ReactNode
    children: ReactNode
    showClose?: boolean
    narrow?: boolean
}

export function Dialog({title, onClose, buttons, children, showClose = true, narrow}: DialogProps): ReactElement {
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                event.stopPropagation()
                onClose()
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    return (
        <div
            className='fixed inset-0 z-dialog flex items-start justify-center bg-black/30 pt-[96px]'
            onMouseDown={onClose}
            role='presentation'
        >
            <div
                className={classnames('Dialog w-full max-w-[420px] px-6 py-5', narrow && 'max-w-[320px]')}
                onMouseDown={(event) => event.stopPropagation()}
                role='dialog'
                aria-modal='true'
            >
                <div className='mb-4 flex items-center justify-between gap-4'>
                    <h2 className='m-0 text-base font-semibold'>{title}</h2>
                    {showClose && (
                        <button
                            type='button'
                            className='IconButton'
                            onClick={onClose}
                            aria-label='关闭'
                        >
                            <Icon name='close' size={16}/>
                        </button>
                    )}
                </div>
                <div>{children}</div>
                {buttons && <div className='mt-6 flex justify-end gap-2'>{buttons}</div>}
            </div>
        </div>
    )
}

/** Full-height side sheet, used for the card detail overlay. */
export function FullScreenDialog({children, onClose, className}: {children: ReactNode, onClose?: () => void, className?: string}): ReactElement {
    return (
        <RootPortal>
            <div
                className={classnames('CardDetailDialog fixed inset-0 z-dialog overflow-hidden bg-main-bg', className)}
                onMouseDown={(event) => {
                    if (event.target === event.currentTarget) {
                        onClose?.()
                    }
                }}
                role='presentation'
            >
                {children}
            </div>
        </RootPortal>
    )
}

/** Port of confirmationDialogBox.tsx. */
export function ConfirmDialog({title, message, confirmText = '删除', cancelText = '取消', onConfirm, onCancel, danger = true}: {
    title: string
    message: ReactNode
    confirmText?: string
    cancelText?: string
    onConfirm: () => void
    onCancel: () => void
    danger?: boolean
}): ReactElement {
    return (
        <Dialog
            title={title}
            onClose={onCancel}
            buttons={
                <>
                    <button type='button' className='Button Button--text' onClick={onCancel}>{cancelText}</button>
                    <button
                        type='button'
                        className={classnames('Button', danger ? 'bg-[#d8002e] text-white' : 'Button--primary')}
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </>
            }
        >
            <div className='text-sm opacity-80'>{message}</div>
        </Dialog>
    )
}
